/**
 * Syncs kiosk UI scale, theme, and controller route → display via loopback state relay.
 */
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSettings } from '../contexts/SettingsContext'
import { useKioskRole, isKioskSettingsHost } from '../hooks/useKioskRole'
import {
  getKioskState,
  patchKioskPayload,
  subscribeKioskState,
  kioskHardReload,
  type KioskStateEnvelope,
} from '../utils/kioskDisplaySync'

function clearZoom() {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const body = document.body
  const app = document.getElementById('root')
  root.classList.remove('kiosk-ui-scaled')
  root.style.zoom = ''
  root.style.overflow = ''
  body.style.width = ''
  body.style.height = ''
  body.style.overflow = ''
  body.style.minHeight = ''
  if (app) {
    app.style.width = ''
    app.style.height = ''
    app.style.minHeight = ''
    app.style.overflow = ''
  }
}

/**
 * CSS zoom enlarges layout boxes vs the visual viewport, which makes
 * h-screen / 100vh shells overflow and scroll. Shrink the layout box by
 * 1/scale so the zoomed result fills the screen exactly.
 * html.kiosk-ui-scaled remaps Tailwind h-screen utilities to 100% so
 * children follow the compensated body height (fixes vertical map center).
 */
function applyZoom(scale: number) {
  if (typeof document === 'undefined') return
  const clamped = Math.min(1.75, Math.max(0.5, Number(scale) || 1))
  const root = document.documentElement
  const body = document.body
  const app = document.getElementById('root')

  root.classList.add('kiosk-ui-scaled')
  root.style.zoom = String(clamped)
  root.style.overflow = 'hidden'
  // After zoom, these layout sizes occupy the full visual viewport.
  const layoutW = `${100 / clamped}vw`
  const layoutH = `${100 / clamped}vh`
  body.style.width = layoutW
  body.style.height = layoutH
  body.style.minHeight = layoutH
  body.style.overflow = 'hidden'
  if (app) {
    app.style.width = '100%'
    app.style.height = '100%'
    app.style.minHeight = '100%'
    app.style.overflow = 'hidden'
  }
  // Leaflet / flex shells need a reflow after scale changes.
  window.requestAnimationFrame(() => {
    window.dispatchEvent(new Event('resize'))
  })
}

function readLocalTheme(): string {
  if (typeof document === 'undefined') return 'light'
  return (
    document.documentElement.getAttribute('data-theme') ||
    localStorage.getItem('theme') ||
    'light'
  )
}

function applyDocumentTheme(theme: string) {
  if (typeof document === 'undefined') return
  const next = String(theme || '').trim()
  if (!next) return
  const current = document.documentElement.getAttribute('data-theme') || ''
  if (current === next) {
    try {
      if (localStorage.getItem('theme') !== next) localStorage.setItem('theme', next)
    } catch {
      /* ignore */
    }
    return
  }
  document.documentElement.setAttribute('data-theme', next)
  try {
    localStorage.setItem('theme', next)
  } catch {
    /* ignore */
  }
}

function routeKey(pathname: string, search: string): string {
  return `${pathname}${search || ''}`
}

function readPayload(envelope: KioskStateEnvelope | null): Record<string, unknown> {
  const p = envelope?.state?.payload
  return p && typeof p === 'object' && !Array.isArray(p) ? (p as Record<string, unknown>) : {}
}

export default function KioskUiScaleSync() {
  const role = useKioskRole()
  const { controllerUiScale, displayUiScale, setDisplayUiScale } = useSettings()
  const location = useLocation()
  const navigate = useNavigate()
  const applyingRemoteRoute = useRef(false)
  /** Display: ignore Settings default until we've seen agent state at least once. */
  const displaySawRemoteScale = useRef(false)
  const [themeEpoch, setThemeEpoch] = useState(0)
  const [theme, setTheme] = useState(readLocalTheme)

  // Keep publish in sync when Appearance changes the DaisyUI theme
  useEffect(() => {
    if (!isKioskSettingsHost(role)) return
    const onTheme = (event: Event) => {
      const detail = (event as CustomEvent<{ theme?: string }>).detail
      const next =
        typeof detail?.theme === 'string' && detail.theme.trim()
          ? detail.theme.trim()
          : readLocalTheme()
      setTheme(next)
      setThemeEpoch((n) => n + 1)
    }
    window.addEventListener('lts-theme-change', onTheme)
    return () => window.removeEventListener('lts-theme-change', onTheme)
  }, [role])

  // Local role applies the matching scale
  useEffect(() => {
    if (!role) {
      clearZoom()
      return
    }
    if (role === 'display') {
      // Prefer cached/local scale only after we've adopted agent state once,
      // or when localStorage already has a non-default value from a prior session.
      // Avoid forcing 100% over a restored agent payload on boot.
      if (!displaySawRemoteScale.current && displayUiScale === 1) return
      applyZoom(displayUiScale)
      return
    }
    applyZoom(controllerUiScale)
  }, [role, controllerUiScale, displayUiScale])

  // Controller / regular: publish scales + theme + current route for display to follow
  useEffect(() => {
    if (!isKioskSettingsHost(role)) return
    if (applyingRemoteRoute.current) return

    const pathname = location.pathname || '/'
    const search = location.search || ''
    const themeToPublish = theme || readLocalTheme()

    void patchKioskPayload(
      {
        controllerUiScale,
        displayUiScale,
        theme: themeToPublish,
        pathname,
        search,
      },
      role || 'controller'
    )
  }, [role, controllerUiScale, displayUiScale, location.pathname, location.search, theme, themeEpoch])

  // Display: follow shared state (scale + theme + route + refresh)
  useEffect(() => {
    if (role !== 'display') return

    const lastRefreshKey = 'ltsKioskLastRefreshAt'
    const lastHardRefreshKey = 'ltsKioskLastHardRefreshAt'
    let lastHandled = 0
    let lastHardHandled = 0
    try {
      lastHandled = Number(sessionStorage.getItem(lastRefreshKey) || 0) || 0
      lastHardHandled = Number(sessionStorage.getItem(lastHardRefreshKey) || 0) || 0
    } catch {
      lastHandled = 0
      lastHardHandled = 0
    }
    let seenInitial = false

    const applyFromEnvelope = (envelope: KioskStateEnvelope | null) => {
      const payload = readPayload(envelope)

      const rawTheme = payload.theme
      if (typeof rawTheme === 'string' && rawTheme.trim()) {
        applyDocumentTheme(rawTheme.trim())
      }

      const rawHard = payload.hardRefreshAt
      const hardRefreshAt = typeof rawHard === 'number' ? rawHard : Number(rawHard)
      if (Number.isFinite(hardRefreshAt) && hardRefreshAt > 0) {
        if (!seenInitial) {
          lastHardHandled = Math.max(lastHardHandled, hardRefreshAt)
          try {
            sessionStorage.setItem(lastHardRefreshKey, String(lastHardHandled))
          } catch {
            /* ignore */
          }
        } else if (hardRefreshAt > lastHardHandled) {
          lastHardHandled = hardRefreshAt
          try {
            sessionStorage.setItem(lastHardRefreshKey, String(hardRefreshAt))
          } catch {
            /* ignore */
          }
          void kioskHardReload()
          return
        }
      }

      const rawRefresh = payload.refreshAt
      const refreshAt = typeof rawRefresh === 'number' ? rawRefresh : Number(rawRefresh)
      if (Number.isFinite(refreshAt) && refreshAt > 0) {
        if (!seenInitial) {
          // Adopt existing nonce on first snapshot so we don't reload on display boot.
          lastHandled = Math.max(lastHandled, refreshAt)
          try {
            sessionStorage.setItem(lastRefreshKey, String(lastHandled))
          } catch {
            /* ignore */
          }
        } else if (refreshAt > lastHandled) {
          lastHandled = refreshAt
          try {
            sessionStorage.setItem(lastRefreshKey, String(refreshAt))
          } catch {
            /* ignore */
          }
          window.location.reload()
          return
        }
      }

      const rawScale = payload.displayUiScale
      const n = typeof rawScale === 'number' ? rawScale : Number(rawScale)
      if (Number.isFinite(n) && n > 0) {
        displaySawRemoteScale.current = true
        applyZoom(n)
        // Cache on the display profile so reboot restores even if agent is briefly empty.
        setDisplayUiScale(n)
      }

      const pathname = typeof payload.pathname === 'string' ? payload.pathname : ''
      if (!pathname) return
      const search = typeof payload.search === 'string' ? payload.search : ''
      const next = routeKey(pathname, search)
      const current = routeKey(window.location.pathname, window.location.search)
      if (next === current) return

      applyingRemoteRoute.current = true
      navigate({ pathname, search }, { replace: true })
      window.setTimeout(() => {
        applyingRemoteRoute.current = false
      }, 50)
    }

    void getKioskState().then((envelope) => {
      applyFromEnvelope(envelope)
      seenInitial = true
      // If agent had no scale yet but this profile cached one, keep using it.
      if (!displaySawRemoteScale.current) {
        try {
          const cached = Number(localStorage.getItem('settings.kioskDisplayUiScale'))
          if (Number.isFinite(cached) && cached > 0) {
            applyZoom(cached)
          }
        } catch {
          /* ignore */
        }
      }
    })
    return subscribeKioskState((envelope) => {
      seenInitial = true
      applyFromEnvelope(envelope)
    })
  }, [role, navigate, setDisplayUiScale])

  return null
}
